/**
 * ListView Component
 *
 * A reusable container component for list views with collapsible header,
 * consistent styling, and standard layout for action bar, list content, and pagination.
 *
 * @example
 * ```tsx
 * <ListView
 *   title="Assessments"
 *   icon={<FileText size={20} />}
 *   expanded={expanded}
 *   onToggleExpand={toggleExpanded}
 * >
 *   <ListActionBar ... />
 *   <DataList ... />
 *   <ListPagination ... />
 * </ListView>
 * ```
 */
import React from 'react';
import { ChevronDown } from 'lucide-react';

export interface ListViewProps {
  /** Title displayed in the header */
  title: string;
  /** Icon displayed before the title */
  icon?: React.ReactNode;
  /** Whether the list view is expanded */
  expanded: boolean;
  /** Callback when expand/collapse is toggled */
  onToggleExpand: () => void;
  /** Content to render when expanded (typically ListActionBar, DataList, and ListPagination) */
  children: React.ReactNode;
  /** Optional CSS class for the container */
  className?: string;
  /** Optional CSS class for the expanded content area */
  contentClassName?: string;
  /** Optional alert banner to show above the content */
  alertBanner?: React.ReactNode;
  /** Whether to show the collapsible header (default: true) */
  showHeader?: boolean;
  /** Whether to hide the header on desktop while preserving it on mobile */
  hideHeaderOnDesktop?: boolean;
  /** Whether to hide the header at every breakpoint */
  hideHeader?: boolean;
}

const ListView: React.FC<ListViewProps> = ({
  title,
  icon,
  expanded,
  onToggleExpand,
  children,
  className = '',
  contentClassName = '',
  alertBanner,
  showHeader = true,
  hideHeaderOnDesktop = false,
  hideHeader = false,
}) => {
  return (
    <div className={`mb-0 md:mb-2 ${className}`}>
      <div className="bg-white md:rounded md:shadow overflow-visible md:border-b md:border-gray-200">
        {/* Collapsible Header */}
        {showHeader && (
          <div
            className={`py-4 px-4 md:p-4 flex items-center justify-between cursor-pointer bg-slate-50 hover:bg-slate-100 active:bg-slate-200 md:bg-white md:hover:bg-slate-50 md:active:bg-slate-100 transition-colors border-b border-slate-200 md:border-gray-200 ${hideHeader ? 'hidden' : hideHeaderOnDesktop ? 'md:hidden' : ''}`}
            onClick={onToggleExpand}
            role="button"
            aria-expanded={expanded}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggleExpand();
              }
            }}
          >
            <h2 className="text-lg font-semibold flex items-center text-slate-800">
              {icon && <span className="mr-3 text-slate-600">{icon}</span>}
              {title}
            </h2>
            <ChevronDown
              size={20}
              className={`transition-transform text-slate-600 ${expanded ? 'rotate-180' : ''}`}
            />
          </div>
        )}

        {/* Expanded Content */}
        {expanded && (
          <div className={`p-3 md:p-4 lg:p-6 overflow-visible ${contentClassName}`}>
            {/* Alert Banner */}
            {alertBanner}

            {/* Main Content */}
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListView;
